import { REPORT_CATEGORIES, REPORT_TYPES } from "../../constants/constants";
import {
  REPORT_FIELD_OUTPUT_TYPES,
  REPORT_FILTER_OPERATOR_TYPES,
  REPORT_FILTER_TYPES,
} from "../../pages/reports/constants";
import { TReportTypesWithUuidSubReportSlug } from "../../pages/reports/type";
import {
  BasicEntityListingPayload,
  LegacyOrganizationEntityListingPayload,
} from "./Admin";

export type ReportsByCategoryPayload = BasicEntityListingPayload & {
  type?: REPORT_TYPES;
  category?: REPORT_CATEGORIES;
  name?: any;
};

export type ReportCategoryFieldsPayload = BasicEntityListingPayload & {
  category: number; // keyof typeof REPORT_CATEGORIES;
};

export type ReportCategoryField = {
  id: number;
  category: number;
  name: string;
  field: string;
  order: number;
  ordering: boolean;
  is_default: boolean;
};

export type SpecificReportPayload = {
  report_id: number;
  type?: REPORT_TYPES;
};

export type TReport = {
  category: number; // keyof typeof REPORT_CATEGORIES;
  created_at: string;
  created_by: string | null;
  id: number;
  identifier: string;
  linked_standard_report: number | null;
  name: string;
  sequence: number;
  type: number;
  updated_at: string;
  shared_by: string | null;
  is_shared_internally: string | null;
  is_shared_externally: string | null;
  is_externally_scheduled: string | null;
  is_group_by_aggregate: boolean;
};

export type TReportStatus = {
  loading: boolean;
};

export type TReportFieldItem = {
  category: number; // keyof typeof REPORT_CATEGORIES;
  created_at: string;
  field: string;
  id: number;
  is_custom: boolean;
  is_default: boolean;
  name: string;
  order: number;
  ordering: boolean;
  updated_at: string;
  all_field_id?: string;
};

export type TReportField = {
  aggregate_function: string;
  column_type: number;
  created_at: string;
  custom_order: number;
  dropdown: boolean;
  field: TReportFieldItem;
  group_order: number;
  id: number;
  identifier: string;
  is_default: boolean;
  is_group_by: boolean;
  name: string;
  name_suffix: string;
  operator: string;
  ordering: boolean;
  output_type: REPORT_FIELD_OUTPUT_TYPES;
  report: number;
  updated_at: string;
  value: number;
  convert_to_whole_number?: boolean;
};

export type TReportFilterRequired = {
  filter_type: REPORT_FILTER_TYPES;
  operator: string;
  param: string;
  value: string;
};

export type TReportFilterAdvanced = {
  report_advance_filters?: {
    sequence: number;
    field: string;
    value: string;
    operator: REPORT_FILTER_OPERATOR_TYPES;
  }[];
};

export type TReportFilter = TReportFilterRequired & {
  created_at?: string;
  field: string; // 'timestamp' | 'deviceid' | 'driverid' | 'geozoneid' | 'groupid';
  id: number;
  is_validate: boolean;
  report: number;
  updated_at: string;
  is_multiple_allowed?: boolean;
} & TReportFilterAdvanced;

export type TReportWithFields = TReport & {
  report_fields: TReportField[];
  report_filters: TReportFilter[];
  is_summary_dependent?: boolean;
  download_formats?: string[]; // response_format values this report can be downloaded or emailed in
};

export type LegacyReportListingPayload =
  LegacyOrganizationEntityListingPayload & {
    accountid: string;
    sub_report_type: TReportTypesWithUuidSubReportSlug;
    start_timestamp?: string;
    end_timestamp?: string;
  } & {
    device_id?: string;
    driver_id?: string;
  };

type TReportInternalShareRolesId = {
  role_id: string;
};

type TReportInternalShareUsersId = {
  user_id: string;
};

export type SharedReportInternallyPayload = {
  report: number;
  type: number;
  report_internal_share_roles?: TReportInternalShareRolesId[];
  report_internal_share_users?: TReportInternalShareUsersId[];
};

type TReportInternalShareRoles = {
  id: number;
  created_at: string;
  updated_at: string;
  account_id: string;
  role_id: string;
  internal_share: number;
};

type TReportInternalShareUsers = {
  id: number;
  created_at: string;
  updated_at: string;
  account_id: string;
  user_id: string;
  internal_share: number;
};

export type SharedReportInternallyResponse = {
  id: number;
  report: number;
  report_internal_share_roles: TReportInternalShareRoles[];
  report_internal_share_users: TReportInternalShareUsers[];
  created_at: string;
  updated_at: string;
  type: number;
  account_id: string;
};

type TReportViaEmailShareEmailsPayload = {
  email: string;
};

export type SharedReportViaEmailPayload = {
  report?: number;
  report_external_share_emails: TReportViaEmailShareEmailsPayload[];
  format: 1 | 2 | 3;
  send_automatically?: 1 | 0;
  frequency?: 1 | 2 | 3 | 4 | 5;
  time_of_the_day?: string;
  day_of_the_week?: 1 | 2 | 3 | 4 | 5 | 6 | 7;
  day_of_the_month?: number;
  day_of_the_month2?: number;
  externalShareId?: number;
};

export type TReportViaEmailShareEmails = {
  id: number;
  created_at: string;
  updated_at: string;
  email: string;
  external_share: number;
};

export type SharedReportViaEmailResponse = {
  id: number;
  report: number;
  report_external_share_emails: TReportViaEmailShareEmails[];
  created_at: string;
  updated_at: string;
  format: 1 | 2 | 3;
  send_automatically: boolean;
  frequency: 1 | 2 | 3 | 4 | 5;
  time_of_the_day: string;
  day_of_the_week: 1 | 2 | 3 | 4 | 5 | 6 | 7;
  day_of_the_month: number;
  day_of_the_month2: number;
};

export type GetSharedReportInternallyPayload = {
  report: number;
};

export type StandardReportFieldPayload = {
  id: string;
};

export type CopyReportPayload = {
  id: number;
  name: string;
};

export type ListingStandardReportFieldPayload = BasicEntityListingPayload & {
  report: number;
  is_custom?: boolean;
};

export enum REPORT_DS_TIME_RANGE {
  CURRENT_MONTH = 1,
  CURRENT_AND_PREVIOUS_MONTH = 2,
  CURRENT_AND_PREVIOUS_TWO_MONTHS = 3,
  PREVIOUS_QUARTER = 4,
}

export type StandardReportPayload = LegacyOrganizationEntityListingPayload & {
  id: string;
  fields?: string[] | string;
} & {
  response_format?: string;
  start_timestamp?: string;
  end_timestamp?: string;
  deviceid?: string;
  driver_id?: string;
  ordering?: string;

  // dashboard filters
  // device_groups?: string[];
  device_group?: any;
  device_id?: any;
  start_time?: number;
  end_time?: number;
  ds_time_range?: REPORT_DS_TIME_RANGE;
};

export type RunCustomReportPayloadField = {
  field: number;
  is_group_by?: boolean;
  group_order?: number;
};

export type RunCustomReportPayload = StandardReportPayload & {
  report_fields?: RunCustomReportPayloadField[];
  report_filters?: TReportFilterRequired[];

  // flags for green average line
  custom_widget_id?: number; // send -1 in case of new unsaved widget
  calculate_average_using?: string; // send identifier of field
};

export type StandardReportFieldResponse = {
  column_type: number;
  created_at: string;
  custom_order: number;
  field: string;
  id: number;
  identifier: string;
  is_default: boolean;
  name: string;
  name_suffix: string;
  ordering: boolean;
  output_type: number;
  report: number;
  updated_at: string;
};

export type ModifyCustomReportPayloadField = {
  field: number;
  field_title?: string; // extra value, added for show/hide columns and save to redux + LS
  is_group_by?: boolean;
  group_order?: number;
  aggregate_function?: number;
  column_type?: number;
  value?: number;
  operator?: string;
};

export type CreateCustomReportPayload = {
  name: string;
  linked_standard_report: number;
  report_fields: ModifyCustomReportPayloadField[];
  report_filters: Partial<TReportFilter>[];
};

export type CreateCustomReportResponse = TReportWithFields & {
  linked_standard_report: number;
};

export type ModifyCustomReportPayload = {
  id: number;
  name?: string;
  report_fields?: ModifyCustomReportPayloadField[];
  report_filters?: Partial<TReportFilter>[];
};

export type RunReportDownloadProcessReponse = {
  id: number;
  created_at: string;
  updated_at: string;
  process: number;
  run_at: string;
  status: number;
  param: {
    report_id: number;
    query_params: {
      id: string;
      fields: string;
      page: string;
      page_size: string;
      search: string;
      start_time: string;
      end_time: string;
      device_id: string;
      timezone: string;
      time_range: string;
      response_format: string;
    };
  };
  started_at: string | null;
  completed_at: string | null;
  user: number;
};

export interface ShareReportListPayload {
  external_share__report: string;
  page: number;
  page_size: number;
  external_share__format: string;
  ordering: string;
  search: string;
}

export type ShareReportListItem = {
  email: string;
  external_share: {
    id?: number;
    created_at: string;
    format: number;
    send_automatically: boolean;
    frequency: number;
    day_of_the_month?: number;
    day_of_the_month2?: number;
    day_of_the_week?: number;
    time_of_the_day: string;
  };
  isactive: number;
  id: number;
};

export type ReportColumnFilterDropdownValuesPayload = {
  report_id: number;
  fields: string;
};

export type ReportColumnFilterDropdownValuesResponseItem = {
  [key: string]: string;
};
export type ReportColumnFilterDropdownValuesResponse =
  ReportColumnFilterDropdownValuesResponseItem[];

// deprecate?
export type DropdownType = {
  id: string;
  description: string;
};
// deprecate?
export type FilterDropDownValuesType = {
  [key: string]: DropdownType[];
};
